import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, ArrowUpCircle, ArrowDownCircle, Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';
import ExportMenu from '../components/ExportMenu';

const MONTHS = ['01','02','03','04','05','06','07','08','09','10','11','12'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function PettyCashPage() {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [monthFilter, setMonthFilter] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [searchText, setSearchText] = useState('');
  const [form, setForm] = useState({ date: '', type: 'Debit', amount: 0, description: '' });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (monthFilter) params.set('month', monthFilter);
      if (yearFilter) params.set('year', yearFilter);
      if (searchText) params.set('search', searchText);
      const [recRes, sumRes] = await Promise.all([
        api.get(`/petty-cash?${params}`),
        api.get(`/petty-cash/summary?${params}`)
      ]);
      setRecords(recRes.data);
      setSummary(sumRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [monthFilter, yearFilter, searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditId(null);
    setForm({ date: new Date().toISOString().split('T')[0], type: 'Debit', amount: 0, description: '' });
    setDialogOpen(true);
  };

  const openEdit = (rec) => {
    setEditId(rec._id);
    setForm({ date: rec.date, type: rec.type, amount: rec.amount, description: rec.description || '' });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.date || !form.amount) { toast.error('Date and amount required'); return; }
    try {
      if (editId) { await api.put(`/petty-cash/${editId}`, form); toast.success('Updated'); }
      else { await api.post('/petty-cash', form); toast.success('Created'); }
      setDialogOpen(false); fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/petty-cash/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const handleDocUpload = async (recordId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/petty-cash/${recordId}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      fetchData();
    } catch { toast.error('Upload failed'); }
  };

  const handleDocDownload = async (recordId, docIndex, filename) => {
    try {
      const response = await api.get(`/petty-cash/${recordId}/documents/${docIndex}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  // Calculate running balance
  const sortedRecords = [...records].sort((a, b) => a.date.localeCompare(b.date));
  let runningBalance = 0;
  const recordsWithBalance = sortedRecords.map(rec => {
    if (rec.type === 'Credit') runningBalance += rec.amount;
    else runningBalance -= rec.amount;
    return { ...rec, balance: runningBalance };
  });

  return (
    <div data-testid="petty-cash-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Petty Cash</h1>
          <p className="text-sm text-[#4F734F] mt-1">Daily cash book for office expenses</p>
        </div>
        <div className="flex gap-2">
          <ExportMenu
            endpoint="/exports/petty-cash"
            params={{ ...(monthFilter ? { month: monthFilter } : {}), ...(yearFilter ? { year: yearFilter } : {}) }}
            filename="petty_cash"
            testIdPrefix="petty-cash"
          />
          <Button onClick={openNew} className="btn-accent gap-2" data-testid="add-cash-btn"><Plus size={16} /> New Entry</Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="stat-card">
            <p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Total Credit</p>
            <p className="font-heading text-2xl font-light text-green-600">{summary.total_credit?.toLocaleString('en-IN')}</p>
          </div>
          <div className="stat-card">
            <p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Total Debit</p>
            <p className="font-heading text-2xl font-light text-red-600">{summary.total_debit?.toLocaleString('en-IN')}</p>
          </div>
          <div className="stat-card">
            <p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Balance</p>
            <p className={`font-heading text-2xl font-light ${summary.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>{summary.balance?.toLocaleString('en-IN')}</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search description..."
          testId="cash-search"
          minWidth="min-w-[280px]"
          className="!flex-none w-full sm:w-80"
        />
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger className="w-40 h-11 input-field"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={m}>{MONTH_NAMES[i]}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger className="w-32 h-11 input-field"><SelectValue /></SelectTrigger>
          <SelectContent>{['2024','2025','2026'].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
        <table className="data-table w-full">
          <thead><tr><th>Date</th><th>Type</th><th>Amount (INR)</th><th>Description</th><th>Docs</th><th>Running Balance</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-olive-400">Loading...</td></tr>
            ) : recordsWithBalance.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-olive-400">No entries found</td></tr>
            ) : recordsWithBalance.map(rec => (
              <tr key={rec._id}>
                <td>{rec.date}</td>
                <td>
                  <div className="flex items-center gap-1.5">
                    {rec.type === 'Credit' ? <ArrowUpCircle size={14} className="text-green-500" /> : <ArrowDownCircle size={14} className="text-red-500" />}
                    <span className={rec.type === 'Credit' ? 'text-green-700' : 'text-red-700'}>{rec.type}</span>
                  </div>
                </td>
                <td className={`font-mono font-medium ${rec.type === 'Credit' ? 'text-green-700' : 'text-red-700'}`}>
                  {rec.type === 'Credit' ? '+' : '-'}{rec.amount?.toLocaleString('en-IN')}
                </td>
                <td>{rec.description}</td>
                <td>
                  <div className="flex items-center gap-1">
                    {(rec.documents || []).map((doc, i) => (
                      <button key={i} onClick={() => handleDocDownload(rec._id, i, doc.filename)} className="p-1 hover:bg-olive-50 rounded-sm" title={doc.filename}>
                        <FileText size={13} className="text-olive-500" />
                      </button>
                    ))}
                    {rec.type === 'Debit' && (
                      <label className="p-1 hover:bg-olive-50 rounded-sm cursor-pointer" title="Upload receipt">
                        <Upload size={13} className="text-[#D4AF37]" />
                        <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleDocUpload(rec._id, e.target.files?.[0])} />
                      </label>
                    )}
                  </div>
                </td>
                <td className={`font-mono font-medium ${rec.balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>{rec.balance?.toLocaleString('en-IN')}</td>
                <td>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(rec)} className="p-1.5 hover:bg-olive-50 rounded-sm"><Pencil size={14} className="text-olive-500" /></button>
                    <button onClick={() => handleDelete(rec._id)} className="p-1.5 hover:bg-red-50 rounded-sm"><Trash2 size={14} className="text-red-500" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? 'Edit' : 'New'} Entry</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))} className="input-field" data-testid="cash-date-input" /></div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm(prev => ({ ...prev, type: v }))}>
                <SelectTrigger className="input-field" data-testid="cash-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Credit">Credit</SelectItem><SelectItem value="Debit">Debit</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Amount (INR)</Label><Input type="number" value={form.amount} onChange={(e) => setForm(prev => ({ ...prev, amount: Number(e.target.value) }))} className="input-field" data-testid="cash-amount-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Description</Label><Textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} className="input-field" data-testid="cash-desc-input" /></div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSubmit} className="btn-primary" data-testid="save-cash-btn">{editId ? 'Update' : 'Create'}</Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
